import { OrgMemberRole } from './org-member';
import { UserVar } from './user';
import { WorkspaceRole } from './workspace-role';
import { WorkspaceServiceMemberDto } from './workspace-service';

export interface WorkspaceDto {
  id: number;
  globalId: string;
  orgId: string;
  userId: number;
  createdAt: number;
  updatedAt: number;
  title: string;
  isDefault: boolean;
  defaultEncryptionKeyId: string | null;
  isNotesLimited: boolean;
  color: string;
  brandingProfileId: string;
  webClientBrandingProfileId: string | null;
  orgType: string;
  orgSubscriptionType: string;
  orgSubscriptionPrivileges: string[];
  avatarStoredFileUUID?: string;
}
export interface WorkspaceMember {
  globalId: string;
  addedByUserId: number;
  userId: number;
  createdAt: number;
  updatedAt: number;
  workspaceId: string;
  type: string;
  role: string;
  encryptRole: string;
  privileges: string[];
  orgId: string;
}
export interface WorkspaceMemberDto {
  globalId: string;
  addedByUserId: number;
  userId: number;
  createdAt: number;
  updatedAt: number;
  workspaceId: string;
  type: string;
  role: string;
  encryptRole: string;
  privileges: string[];
  orgId: string;
}

export interface WorkspaceInvite {
  id: number;
  orgInviteId: number;
  workspaceId: string;
  role: string;
  email: string;
}

export interface WorkspaceAvatarDto {
  userId: number;
  workspaceId: string;
  storedFileUUID: string;
  createdAt: number;
  updatedAt: number;
}

export interface WorkspaceHtmlDto {
  id: number;
  globalId: string;
  userId: number;
  workspaceId: string;
  orgId: string;
  name: string;
  htmlHead: string;
  htmlBody: string;
  showWebClient: boolean;
  showPublicLinks: boolean;
  createdAt: number;
  updatedAt: number;
  bodyPosition: string;
}

export interface WorkspaceUsageDto {
  traffic: {
    current: number;
    max: number;
  };
  encryptionKeys: {
    current: number;
    max: number;
  };
  members: {
    current: number;
    max: number;
  };
}

export function workspaceMembersFromDto(wsMembers: WorkspaceServiceMemberDto[]): WorkspaceMember[] {
  return wsMembers.map((wsMember) => workspaceMemberFromDto(wsMember));
}
export function workspaceMemberFromDto(dto: WorkspaceServiceMemberDto): WorkspaceMember {
  return {
    globalId: dto.globalId,
    addedByUserId: dto.addedByUserId,
    userId: dto.userId,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    workspaceId: dto.workspaceId,
    type: dto.type,
    role: dto.role,
    encryptRole: '',
    privileges: dto.privileges,
    orgId: 'orgId' in dto && typeof dto.orgId === 'string' ? dto.orgId : '',
  };
}

export interface Workspace {
  id: number;
  globalId: string;
  orgId: string;
  userId: number;
  createdAt: number;
  updatedAt: number;
  title: string;
  isDefault: boolean;
  defaultEncryptionKeyId: string | null;
  isNotesLimited: boolean;
  color: string;
  brandingProfileId: string;
  webClientBrandingProfileId: string | null;
  orgType: string;
  orgSubscriptionType: string;
  orgSubscriptionPrivileges: string[];
}

export interface WorkspaceAvatar {
  userId: number;
  workspaceId: string;
  storedFileUUID: string;
  createdAt: number;
  updatedAt: number;
}

export interface GetWorkspaceParams {
  workspaceId: string;
}

export interface CreateWorkspaceParams {
  orgId: string;
  title: string;
  skipLimits: boolean;
}

export interface CreateWorkspaceOptions {
  userId: number;
}

export interface GetWorkspaceOptions {
  userId: number;
  /**
   * fetch workspaces for speicific org
   */
  orgId?: string;
}

export interface GetLatestWorkspaceIdOptions extends GetWorkspaceOptions {
  orgId: string;
}

export interface GetLatestWorkspaceIdByUserVarsOptions extends GetLatestWorkspaceIdOptions {
  userVars: UserVar[];
}

export interface SetLatestWorkspaceIdOptions extends GetWorkspaceOptions {
  userId: number;
  orgId: string;
  workspaceId: string;
}

export interface GetWorkspacesAvatarsParams {
  workspaces: Workspace[];
}

export enum WorkspacePrivilege {
  CAN_READ = 'canRead',
  CAN_COMMENT = 'canComment',
  CAN_EDIT = 'canEdit',
  CAN_MANAGE_PUBLIC_LINKS = 'canManagePublicLinks',
  CAN_MANAGE_TAGS = 'canManageTags',
  CAN_DELETE_NODES = 'canDeleteNodes',
  CAN_RENAME_FOLDERS = 'canRenameFolders',
  CAN_MOVE_NODES = 'canMoveNodes',
  CAN_MANAGE_MEMBERS = 'canManageMembers',
  CAN_MANAGE_PERMISSIONS = 'canManagePermissions',
  CAN_ACCESS_BLOT_LOCKING = 'canAccessBlotLocking',
}

export interface WorkspaceAccess {
  type: OrgMemberRole;
  role: WorkspaceRole;
  encryptRole: string | null;
  privileges: WorkspacePrivilege[] | null;
  reduced: boolean;
  orgId: string | null;
}

export interface OrgWorkspaceAccess {
  workspaceId: string;
  access: WorkspaceAccess;
}
